/**
 * Admin Blog Editor — list, create, edit, schedule, publish, delete posts.
 * Admin-only. Mounted at /dashboard/admin/blog.
 *
 * Uses a markdown textarea (no heavy WYSIWYG dep) with live HTML preview
 * via the backend's md→html on save. Cover image uploads go through
 * Emergent Object Storage.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  FileText, Plus, Save, Trash2, Upload, Eye, ArrowLeft, Calendar,
  CheckCircle2, Circle, Clock, Search, ExternalLink,
} from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";
import { useAuth } from "../../lib/auth";

function AdminGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function StatusBadge({ status, publishAt }) {
  const map = {
    draft: { Icon: Circle, label: "Draft", cls: "text-black/50 bg-black/5" },
    scheduled: { Icon: Clock, label: "Scheduled", cls: "text-amber-700 bg-amber-100" },
    published: { Icon: CheckCircle2, label: "Live", cls: "text-emerald-700 bg-emerald-100" },
  };
  const m = map[status] || map.draft;
  const future =
    status === "scheduled" && publishAt && new Date(publishAt) > new Date();
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] uppercase tracking-widest ${m.cls}`}>
      <m.Icon className="w-3 h-3" />
      {m.label}
      {future && publishAt && (
        <span className="opacity-60">
          · {new Date(publishAt).toLocaleDateString()}
        </span>
      )}
    </span>
  );
}

function PostListView({ posts, onSelect, onCreate, onRefresh, query, setQuery }) {
  const filtered = useMemo(() => {
    if (!query.trim()) return posts;
    const q = query.toLowerCase();
    return posts.filter(
      (p) =>
        (p.title || "").toLowerCase().includes(q) ||
        (p.slug || "").toLowerCase().includes(q),
    );
  }, [posts, query]);

  return (
    <div data-testid="admin-blog-list">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Admin · Resources</p>
          <h1 className="font-serif font-light text-4xl">Blog Editor</h1>
          <p className="text-[var(--muted)] mt-2 text-sm">
            Author SEO articles. Schedule publishing. Replace stub posts with real editorial.
          </p>
        </div>
        <button
          onClick={onCreate}
          className="btn-gold whitespace-nowrap"
          data-testid="admin-blog-new"
        >
          <Plus className="w-4 h-4" /> New Post
        </button>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-black/30" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by title or slug…"
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[var(--line)] bg-white text-sm focus:border-[var(--gold)] outline-none"
            data-testid="admin-blog-search"
          />
        </div>
        <button onClick={onRefresh} className="btn-outline-gold text-sm">Refresh</button>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-black/10 rounded-2xl bg-white/40">
          <FileText className="w-10 h-10 text-black/20 mx-auto mb-4" />
          <p className="font-serif text-2xl">No posts yet</p>
          <p className="text-sm text-black/50 mt-2">Create your first article to replace the curated stubs at /blog.</p>
          <button onClick={onCreate} className="btn-gold mt-6">
            <Plus className="w-4 h-4" /> Write First Post
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((p) => (
            <div
              key={p.post_id}
              className="bg-white border border-[var(--line)] rounded-xl p-5 hover:border-[var(--gold)] transition cursor-pointer"
              onClick={() => onSelect(p.post_id)}
              data-testid={`admin-blog-row-${p.slug}`}
            >
              <div className="flex items-start gap-4">
                {p.cover_path ? (
                  <img
                    src={`${BACKEND_URL}/api/admin/blog/${p.post_id}/cover`}
                    alt=""
                    className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-black/5 flex-shrink-0 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-black/30" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-serif text-xl">{p.title}</h3>
                    <StatusBadge status={p.status} publishAt={p.publish_at} />
                  </div>
                  <p className="text-sm text-black/60 mt-1 truncate">{p.excerpt || "—"}</p>
                  <p className="text-[11px] uppercase tracking-widest text-black/40 mt-2 font-mono">/blog/{p.slug}</p>
                </div>
                <Link
                  to={`/blog/${p.slug}`}
                  target="_blank"
                  rel="noopener"
                  onClick={(e) => e.stopPropagation()}
                  className="text-[var(--gold)] text-sm flex-shrink-0"
                >
                  <ExternalLink className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PostEditorView({ postId, onBack, onSaved }) {
  const isNew = !postId;
  const [post, setPost] = useState({
    title: "",
    slug: "",
    excerpt: "",
    body_markdown: "",
    tags: [],
    author: "LockedInForever Editorial",
    status: "draft",
    publish_at: "",
  });
  const [loaded, setLoaded] = useState(isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [coverVersion, setCoverVersion] = useState(0);

  useEffect(() => {
    if (isNew) return;
    api
      .get(`/admin/blog/${postId}`)
      .then((r) => {
        setPost({
          title: r.data.title || "",
          slug: r.data.slug || "",
          excerpt: r.data.excerpt || "",
          body_markdown: r.data.body_markdown || "",
          tags: r.data.tags || [],
          author: r.data.author || "LockedInForever Editorial",
          status: r.data.status || "draft",
          publish_at: r.data.publish_at || "",
          cover_path: r.data.cover_path,
        });
        setLoaded(true);
      })
      .catch((e) => {
        toast.error(formatApiError(e));
        onBack();
      });
  }, [postId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save() {
    if (!post.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...post };
      if (!payload.publish_at) payload.publish_at = null;
      if (isNew) {
        const r = await api.post("/admin/blog", payload);
        toast.success("Post created");
        onSaved(r.data.post_id);
      } else {
        await api.put(`/admin/blog/${postId}`, payload);
        toast.success("Saved");
        onSaved(postId);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await api.delete(`/admin/blog/${postId}`);
      toast.success("Deleted");
      onBack();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function uploadCover(e) {
    const file = e.target.files?.[0];
    if (!file || !postId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/blog/${postId}/cover`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Cover uploaded");
      setCoverVersion((v) => v + 1);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  }

  if (!loaded) return <div className="text-sm text-black/40">Loading…</div>;

  return (
    <div data-testid="admin-blog-editor" className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <button
          onClick={onBack}
          className="text-sm text-black/60 hover:text-black flex items-center gap-2"
          data-testid="admin-blog-back"
        >
          <ArrowLeft className="w-4 h-4" /> All posts
        </button>
        <div className="flex items-center gap-2">
          {!isNew && (
            <button
              onClick={remove}
              className="text-sm text-red-600 px-3 py-2 rounded-lg hover:bg-red-50 flex items-center gap-2"
              data-testid="admin-blog-delete"
            >
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
          <button
            onClick={save}
            disabled={saving}
            className="btn-gold"
            data-testid="admin-blog-save"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-5">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-black/50">Title</label>
            <input
              value={post.title}
              onChange={(e) => setPost({ ...post, title: e.target.value })}
              placeholder="100 Best Wedding Songs"
              className="w-full mt-1 px-4 py-3 rounded-xl border border-[var(--line)] bg-white font-serif text-2xl outline-none focus:border-[var(--gold)]"
              data-testid="admin-blog-title"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-widest text-black/50">Slug</label>
            <input
              value={post.slug}
              onChange={(e) => setPost({ ...post, slug: e.target.value })}
              placeholder="auto-generated from title"
              className="w-full mt-1 px-4 py-2.5 rounded-xl border border-[var(--line)] bg-white font-mono text-sm outline-none focus:border-[var(--gold)]"
              data-testid="admin-blog-slug"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-widest text-black/50">Excerpt</label>
            <textarea
              value={post.excerpt}
              onChange={(e) => setPost({ ...post, excerpt: e.target.value })}
              placeholder="A 1-2 sentence hook for SEO snippets and social cards."
              rows={2}
              className="w-full mt-1 px-4 py-3 rounded-xl border border-[var(--line)] bg-white text-sm outline-none focus:border-[var(--gold)]"
              data-testid="admin-blog-excerpt"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-widest text-black/50">
              Body (Markdown — # / ## / ### / **bold** / _italic_ / - list / [link](url))
            </label>
            <textarea
              value={post.body_markdown}
              onChange={(e) => setPost({ ...post, body_markdown: e.target.value })}
              placeholder="# Section heading\n\nWrite your article here…"
              rows={20}
              className="w-full mt-1 px-4 py-3 rounded-xl border border-[var(--line)] bg-white font-mono text-sm outline-none focus:border-[var(--gold)]"
              data-testid="admin-blog-body"
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          <div className="bg-white border border-[var(--line)] rounded-2xl p-5">
            <p className="text-[11px] uppercase tracking-widest text-black/50 mb-3">Status</p>
            <div className="space-y-2">
              {[
                { v: "draft", l: "Draft", desc: "Hidden from /blog" },
                { v: "scheduled", l: "Scheduled", desc: "Auto-publishes at chosen time" },
                { v: "published", l: "Published", desc: "Live at /blog/" + (post.slug || "...") },
              ].map((opt) => (
                <label
                  key={opt.v}
                  className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer ${
                    post.status === opt.v
                      ? "border-[var(--gold)] bg-[#C8A96B]/5"
                      : "border-[var(--line)]"
                  }`}
                  data-testid={`admin-blog-status-${opt.v}`}
                >
                  <input
                    type="radio"
                    checked={post.status === opt.v}
                    onChange={() => setPost({ ...post, status: opt.v })}
                    className="mt-1"
                  />
                  <div>
                    <p className="text-sm font-medium">{opt.l}</p>
                    <p className="text-xs text-black/50">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>

            {(post.status === "scheduled" || post.status === "published") && (
              <div className="mt-4">
                <label className="text-[11px] uppercase tracking-widest text-black/50 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Publish at
                </label>
                <input
                  type="datetime-local"
                  value={post.publish_at ? post.publish_at.slice(0, 16) : ""}
                  onChange={(e) =>
                    setPost({
                      ...post,
                      publish_at: e.target.value
                        ? new Date(e.target.value).toISOString()
                        : "",
                    })
                  }
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-[var(--line)] bg-white text-sm outline-none focus:border-[var(--gold)]"
                  data-testid="admin-blog-publish-at"
                />
                <p className="text-[11px] text-black/40 mt-1">Leave empty to publish immediately.</p>
              </div>
            )}
          </div>

          <div className="bg-white border border-[var(--line)] rounded-2xl p-5">
            <p className="text-[11px] uppercase tracking-widest text-black/50 mb-3">Cover Image</p>
            {!isNew ? (
              <>
                {post.cover_path ? (
                  <img
                    src={`${BACKEND_URL}/api/admin/blog/${postId}/cover?v=${coverVersion}`}
                    alt=""
                    className="w-full aspect-[16/9] object-cover rounded-xl mb-3"
                  />
                ) : (
                  <div className="w-full aspect-[16/9] bg-black/5 rounded-xl mb-3 flex items-center justify-center text-sm text-black/40">
                    No cover yet
                  </div>
                )}
                <label className="btn-outline-gold w-full justify-center cursor-pointer text-sm">
                  <Upload className="w-4 h-4" />
                  {uploading ? "Uploading…" : "Upload cover"}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={uploadCover}
                    className="hidden"
                    disabled={uploading}
                    data-testid="admin-blog-cover-input"
                  />
                </label>
                <p className="text-[11px] text-black/40 mt-2">JPG/PNG/WebP, max 8 MB.</p>
              </>
            ) : (
              <p className="text-xs text-black/40">Save the post first to upload a cover image.</p>
            )}
          </div>

          <div className="bg-white border border-[var(--line)] rounded-2xl p-5">
            <p className="text-[11px] uppercase tracking-widest text-black/50 mb-3">Author</p>
            <input
              value={post.author}
              onChange={(e) => setPost({ ...post, author: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-[var(--line)] bg-white text-sm outline-none focus:border-[var(--gold)]"
              data-testid="admin-blog-author"
            />
          </div>

          {!isNew && post.status === "published" && (
            <Link
              to={`/blog/${post.slug}`}
              target="_blank"
              rel="noopener"
              className="btn-outline-gold w-full justify-center text-sm"
            >
              <Eye className="w-4 h-4" /> View live
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminBlogPage() {
  const [posts, setPosts] = useState([]);
  const [editingId, setEditingId] = useState(null); // null = list view; "new" = new post; "<id>" = edit
  const [query, setQuery] = useState("");

  async function load() {
    try {
      const r = await api.get("/admin/blog");
      setPosts(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <AdminGate>
      <div data-testid="admin-blog-page">
        {editingId === null ? (
          <PostListView
            posts={posts}
            query={query}
            setQuery={setQuery}
            onCreate={() => setEditingId("new")}
            onSelect={(id) => setEditingId(id)}
            onRefresh={load}
          />
        ) : (
          <PostEditorView
            postId={editingId === "new" ? null : editingId}
            onBack={() => {
              setEditingId(null);
              load();
            }}
            onSaved={(newId) => {
              setEditingId(newId);
              load();
            }}
          />
        )}
      </div>
    </AdminGate>
  );
}
